import * as React from "react";
import { Loader2, Filter, X, DollarSign, ShoppingBag, ChevronLeft, ChevronRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { useAdminPurchaseReport, type PurchaseReportFilters } from "@/hooks/useAdmin";

const STATUSES = ["", "Active", "Trial", "PastDue", "Cancelled"];
const TYPES = ["", "email", "hosting", "portfolio"];
const CYCLES = ["", "monthly", "quarterly", "annually"];

export function AdminReportsPage() {
  const [draft, setDraft] = React.useState<PurchaseReportFilters>({ page: 1, pageSize: 20 });
  const [filters, setFilters] = React.useState<PurchaseReportFilters>({ page: 1, pageSize: 20 });
  const { data, isLoading, isFetching } = useAdminPurchaseReport(filters);

  function apply() {
    setFilters({ ...draft, page: 1 });
  }
  function reset() {
    const cleared = { page: 1, pageSize: 20 };
    setDraft(cleared);
    setFilters(cleared);
  }
  function setPage(p: number) {
    setFilters((f) => ({ ...f, page: p }));
  }

  const currency = data?.currency ?? "USD";
  const maxRevenue = Math.max(1, ...(data?.daily ?? []).map((d) => d.revenue));

  return (
    <div className="space-y-5">
      <div>
        <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">Reports</h2>
        <p className="text-sm text-muted-foreground">Daily purchases and customer purchase history.</p>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="From">
              <Input type="date" value={draft.from ?? ""} onChange={(e) => setDraft({ ...draft, from: e.target.value || undefined })} />
            </Field>
            <Field label="To">
              <Input type="date" value={draft.to ?? ""} onChange={(e) => setDraft({ ...draft, to: e.target.value || undefined })} />
            </Field>
            <Field label="Customer (email or name)">
              <Input placeholder="search..." value={draft.customer ?? ""} onChange={(e) => setDraft({ ...draft, customer: e.target.value || undefined })} />
            </Field>
            <Field label="Plan type">
              <Select value={draft.planType ?? ""} onChange={(v) => setDraft({ ...draft, planType: v || undefined })} options={TYPES} />
            </Field>
            <Field label="Status">
              <Select value={draft.status ?? ""} onChange={(v) => setDraft({ ...draft, status: v || undefined })} options={STATUSES} />
            </Field>
            <Field label="Billing cycle">
              <Select value={draft.cycle ?? ""} onChange={(v) => setDraft({ ...draft, cycle: v || undefined })} options={CYCLES} />
            </Field>
            <div className="flex items-end gap-2 lg:col-span-2">
              <Button onClick={apply} disabled={isFetching}>
                {isFetching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
                Apply filters
              </Button>
              <Button variant="outline" onClick={reset}><X className="h-4 w-4" /> Reset</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      <div className="grid gap-3 sm:grid-cols-2">
        <SummaryCard icon={<ShoppingBag className="h-5 w-5" />} label="Purchases" value={data ? String(data.total_count) : "—"} />
        <SummaryCard icon={<DollarSign className="h-5 w-5" />} label="Revenue (recurring + one-time)" value={data ? money(data.total_revenue, currency) : "—"} />
      </div>

      {/* Daily mini-chart */}
      <Card>
        <CardContent className="py-4">
          <p className="mb-3 text-sm font-semibold">Daily purchases</p>
          {isLoading ? (
            <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : (data?.daily.length ?? 0) === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">No purchases in this range.</p>
          ) : (
            <div className="flex items-end gap-1.5 overflow-x-auto pb-1" style={{ minHeight: 96 }}>
              {data!.daily.map((d) => (
                <div key={d.date} className="flex min-w-[26px] flex-1 flex-col items-center gap-1" title={`${d.date}: ${d.count} · ${money(d.revenue, currency)}`}>
                  <div
                    className="w-full rounded-t bg-primary/80"
                    style={{ height: `${Math.round((d.revenue / maxRevenue) * 72) + 4}px` }}
                  />
                  <span className="text-[9px] text-muted-foreground">{d.date.slice(5)}</span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Purchase history table */}
      <Card>
        <CardContent className="px-0 py-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-muted-foreground">
                  <th className="px-4 py-3">Customer</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Cycle</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Amount</th>
                  <th className="px-4 py-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-primary" /></td></tr>
                ) : (data?.items.length ?? 0) === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No purchases match these filters.</td></tr>
                ) : (
                  data!.items.map((r) => (
                    <tr key={r.subscription_id} className="border-b border-border/60 last:border-0">
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.customer_name || "—"}</div>
                        <div className="text-xs text-muted-foreground">{r.customer_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {r.plan_name}
                        {r.plan_type && <span className="ml-1.5 rounded-full bg-muted px-1.5 py-0.5 text-[10px] capitalize text-muted-foreground">{r.plan_type}</span>}
                      </td>
                      <td className="px-4 py-3">{cycleLabel(r.billing_months)}</td>
                      <td className="px-4 py-3"><StatusPill status={r.status} /></td>
                      <td className="px-4 py-3 text-right font-medium">{money(r.amount, currency)}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.started_at.slice(0, 10)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {data && data.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-border px-4 py-3 text-sm">
              <span className="text-muted-foreground">Page {data.page} of {data.total_pages}</span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={data.page <= 1} onClick={() => setPage(data.page - 1)}><ChevronLeft className="h-4 w-4" /></Button>
                <Button variant="outline" size="sm" disabled={data.page >= data.total_pages} onClick={() => setPage(data.page + 1)}><ChevronRight className="h-4 w-4" /></Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}

function Select({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: string[] }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm capitalize ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      {options.map((o) => <option key={o} value={o}>{o === "" ? "All" : o}</option>)}
    </select>
  );
}

function SummaryCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-accentSoft text-brand-accentDark">{icon}</div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="font-display text-xl font-bold">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls = status === "Active" ? "bg-brand-green/10 text-brand-green"
    : status === "Cancelled" ? "bg-destructive/10 text-destructive"
    : status === "PastDue" ? "bg-amber-500/10 text-amber-600"
    : "bg-muted text-muted-foreground";
  return <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}>{status}</span>;
}

function cycleLabel(months: number) {
  return months === 12 ? "Annually" : months === 3 ? "Quarterly" : "Monthly";
}
function money(n: number, currency: string) {
  const symbol = currency === "USD" ? "$" : `${currency} `;
  return `${symbol}${n.toFixed(2)}`;
}
